import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Search, Navigation, Loader2, Globe, AlertCircle } from "lucide-react";
import { POPULAR_CITIES, OTHER_CITIES, useMarketplace, type City } from "./marketplace-context";

interface CitySelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCity: City;
  onSelectCity: (city: City) => void;
}

/* ── Minimal line-art landmark SVGs for the top 10 popular cities ── */
const CityLandmarks: Record<string, React.ReactNode> = {
  Mumbai: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M16 52V30H48V52"/><path d="M24 52V42C24 37.6 27.6 34 32 34C36.4 34 40 37.6 40 42V52"/>
      <path d="M10 52H54"/><path d="M20 30V24H44V30"/><path d="M16 30H20M44 30H48"/>
      <path d="M14 30V26C14 24.9 14.9 24 16 24"/><path d="M50 30V26C50 24.9 49.1 24 48 24"/>
      <path d="M18 24C18 21 19.5 18 22 18C24.5 18 26 21 26 24"/><path d="M38 24C38 21 39.5 18 42 18C44.5 18 46 21 46 24"/>
      <path d="M26 24C26 20 28 16 32 16C36 16 38 20 38 24"/>
    </svg>
  ),
  Delhi: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M18 52V20C18 18.9 18.9 18 20 18H44C45.1 18 46 18.9 46 20V52"/>
      <path d="M24 52V36C24 31.6 27.6 28 32 28C36.4 28 40 31.6 40 36V52"/>
      <path d="M12 52H52"/><path d="M16 18H48"/><path d="M22 18V14H42V18"/><path d="M26 14V10H38V14"/>
    </svg>
  ),
  Bengaluru: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M8 52H56"/><path d="M14 52V30H50V52"/>
      <path d="M28 30C28 24 29.5 20 32 20C34.5 20 36 24 36 30"/><line x1="32" y1="20" x2="32" y2="14"/>
      <line x1="18" y1="30" x2="18" y2="52"/><line x1="22" y1="30" x2="22" y2="52"/>
      <line x1="42" y1="30" x2="42" y2="52"/><line x1="46" y1="30" x2="46" y2="52"/>
      <path d="M24 52V42H40V52"/><path d="M28 42V38H36V42"/>
    </svg>
  ),
  Hyderabad: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M14 52H50"/><path d="M20 52V28H44V52"/>
      <path d="M24 52V40C24 35.6 27.6 32 32 32C36.4 32 40 35.6 40 40V52"/>
      <path d="M28 28V22H36V28"/>
      <path d="M16 52V18C16 16.5 17 16 18 16C19 16 20 16.5 20 18"/><path d="M44 52V18C44 16.5 45 16 46 16C47 16 48 16.5 48 18"/>
      <path d="M15 16C15 14 16.5 12 18 12C19.5 12 21 14 21 16"/><path d="M43 16C43 14 44.5 12 46 12C47.5 12 49 14 49 16"/>
    </svg>
  ),
  Chennai: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M14 52H50"/><path d="M20 52V44H44V52"/><path d="M22 44V36H42V44"/>
      <path d="M24 36V28H40V36"/><path d="M26 28V20H38V28"/>
      <path d="M30 20V16C30 14.5 31 14 32 14C33 14 34 14.5 34 16V20"/>
      <line x1="18" y1="44" x2="46" y2="44"/><line x1="20" y1="36" x2="44" y2="36"/><line x1="22" y1="28" x2="42" y2="28"/>
    </svg>
  ),
  Kolkata: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M6 48H58"/><path d="M16 48V20L22 14L28 20V48"/><path d="M36 48V20L42 14L48 20V48"/>
      <path d="M6 30C16 30 22 20 22 20"/><path d="M28 20C28 20 32 32 36 20"/><path d="M42 20C42 20 48 30 58 30"/>
      <line x1="12" y1="30" x2="12" y2="48"/><line x1="32" y1="30" x2="32" y2="48"/><line x1="52" y1="30" x2="52" y2="48"/>
    </svg>
  ),
  Pune: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M10 52H54"/><path d="M18 52V22L32 16L46 22V52"/>
      <path d="M28 52V34C28 31.8 29.8 30 32 30C34.2 30 36 31.8 36 34V52"/>
      <circle cx="24" cy="34" r="2"/><circle cx="40" cy="34" r="2"/>
      <path d="M14 22H50"/><path d="M20 22V52"/><path d="M44 22V52"/>
    </svg>
  ),
  Ahmedabad: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M12 52V26C12 18 20 12 32 12C44 12 52 18 52 26V52"/>
      <path d="M10 52H54"/><path d="M20 52V30C20 26 24 22 32 22C40 22 44 26 44 30V52"/>
      <path d="M32 12V22"/><path d="M22 18C24 20 28 22 32 22"/><path d="M42 18C40 20 36 22 32 22"/>
    </svg>
  ),
  Chandigarh: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M32 52V36"/><path d="M26 52H38"/>
      <path d="M32 36C28 36 20 32 18 28C16 25 17 22 19 20C21 19 24 22 27 24C27 20 24 12 27 10C29 8 32 12 32 16C33 12 36 8 38 10C40 12 38 18 37 24C39 20 42 16 44 18C46 20 44 24 40 28C43 28 47 29 48 31C49 33 47 36 43 36H32"/>
    </svg>
  ),
  Jaipur: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M10 52H54"/><path d="M14 52V40H50V52"/><path d="M18 40V32H46V40"/><path d="M24 32V22H40V32"/>
      <path d="M18 48C18 46 19.5 44.5 21.5 44.5C23.5 44.5 25 46 25 48"/>
      <path d="M30 48C30 46 31.5 44.5 33 44.5C34.5 44.5 36 46 36 48"/>
      <path d="M40 48C40 46 41.5 44.5 43 44.5C44.5 44.5 46 46 46 48"/>
      <path d="M23 36C23 34 25 32.5 28 32.5C31 32.5 33 34 33 36"/>
      <path d="M35 36C35 34 37 32.5 40 32.5C43 32.5 45 34 45 36"/>
      <path d="M29 27C29 24.5 30.5 23 32 23C33.5 23 35 24.5 35 27"/>
      <path d="M30 22C30 20 32 18 32 18C32 18 34 20 34 22"/>
    </svg>
  ),
  Kochi: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M6 52H58"/><path d="M14 52L36 18"/><path d="M20 52L30 36"/>
      <path d="M36 18L50 38H26L36 18Z" fill="currentColor" fillOpacity="0.06"/>
      <path d="M50 38L54 46"/><path d="M26 38L22 46"/><path d="M14 52L8 42"/><path d="M18 52L12 44"/>
    </svg>
  ),
  Lucknow: (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M10 52H54"/><path d="M14 52C14 28 24 16 32 16C40 16 50 28 50 52"/>
      <path d="M20 52C20 36 24 24 32 24C40 24 44 36 44 52"/>
      <path d="M26 52C26 42 28 40 32 40C36 40 38 42 38 52"/>
      <line x1="12" y1="52" x2="12" y2="34"/><line x1="52" y1="52" x2="52" y2="34"/>
      <circle cx="12" cy="33" r="1.5"/><circle cx="52" cy="33" r="1.5"/>
    </svg>
  ),
};

/* ── Alphabet array for the All Cities filter ── */
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

/* ── Combine all cities (except "All India") for the All Cities section ── */
const ALL_CITIES_SORTED = [...POPULAR_CITIES, ...OTHER_CITIES].sort((a, b) => a.localeCompare(b));

/* ── Top cities shown with landmark icons ── */
const TOP_ICON_CITIES = [
  "Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Chennai",
  "Kolkata", "Pune", "Ahmedabad", "Chandigarh", "Jaipur", "Kochi", "Lucknow",
] as const;

export const CitySelectionModal: React.FC<CitySelectionModalProps> = ({
  isOpen,
  onClose,
  currentCity,
  onSelectCity,
}) => {
  const { detectLocation, isDetectingLocation } = useMarketplace();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [detectError, setDetectError] = useState<string | null>(null);
  const [isLocalDetecting, setIsLocalDetecting] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const letterRefs = useRef<Record<string, HTMLDivElement | null>>({});

  /* Close on Escape */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  /* Focus search + reset on open */
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 150);
      setSearchQuery("");
      setActiveLetter(null);
      setDetectError(null);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  /* Filtered search results */
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    const all = ["All India", ...ALL_CITIES_SORTED];
    return all.filter((c) => c.toLowerCase().includes(q));
  }, [searchQuery]);

  /* All Cities grouped by first letter */
  const citiesByLetter = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const c of ALL_CITIES_SORTED) {
      const letter = c[0].toUpperCase();
      if (!map[letter]) map[letter] = [];
      map[letter].push(c);
    }
    return map;
  }, []);

  /* Scroll to letter group inside inner scroll container */
  const scrollToLetter = (letter: string) => {
    setActiveLetter(letter);
    const el = letterRefs.current[letter];
    const container = scrollContainerRef.current;
    if (el && container) {
      const top = el.offsetTop - container.offsetTop;
      container.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleUseCurrentLocation = async () => {
    setDetectError(null);
    setIsLocalDetecting(true);
    try {
      const detected = await detectLocation(true);
      if (detected) {
        onSelectCity(detected);
        onClose();
      } else {
        setDetectError("Unable to detect your exact city via GPS or IP. Please select your city from the list below.");
      }
    } catch {
      setDetectError("Location access was denied or timed out. Please select your city manually.");
    } finally {
      setIsLocalDetecting(false);
    }
  };

  const isSearchActive = searchQuery.trim().length > 0;
  const isBusy = isLocalDetecting || isDetectingLocation;

  if (!isOpen) return null;

  const handleSelect = (city: string) => {
    onSelectCity(city as City);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-[720px] max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden my-auto animate-in fade-in zoom-in-95 duration-150"
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Select Location</h2>
            <p className="text-xs text-gray-500 mt-0.5">Choose your city to see relevant ads and prices near you</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 -mr-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ── Search ── */}
        <div className="px-6 pb-3 shrink-0">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search city, area or locality (e.g. Mumbai, Bangalore, Hyderabad)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 pl-10 pr-4 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 transition placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* ── Scrollable content ── */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-6 pb-6">
          {/* Detect Location Button */}
          <div className="mb-4">
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              disabled={isBusy}
              className={`w-full flex items-center justify-between p-3.5 rounded-xl border transition-all ${
                isBusy
                  ? "bg-indigo-50/80 border-indigo-200 text-indigo-700 cursor-wait"
                  : "bg-gradient-to-r from-indigo-50/70 to-blue-50/50 hover:from-indigo-50 hover:to-blue-50 border-indigo-100 hover:border-indigo-300 text-indigo-900 group shadow-sm hover:shadow"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-200 shrink-0">
                  {isBusy ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Navigation className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  )}
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    {isBusy ? "Detecting your location..." : "Use Current Location"}
                  </div>
                  <div className="text-xs text-gray-500">
                    {isBusy ? "Fetching accurate GPS coordinates..." : "Detect exact city using device GPS"}
                  </div>
                </div>
              </div>
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-lg border shadow-xs transition ${
                isBusy
                  ? "bg-indigo-100 border-indigo-300 text-indigo-700"
                  : "bg-white border-indigo-200 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white"
              }`}>
                {isBusy ? "Detecting..." : "Detect GPS"}
              </span>
            </button>

            {detectError && (
              <div className="mt-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2 animate-in fade-in">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <span>{detectError}</span>
              </div>
            )}
          </div>

          {isSearchActive ? (
            /* ── Search Results ── */
            <div className="space-y-2 pt-2">
              {searchResults.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-8">
                  No cities found matching "<span className="font-medium text-gray-600">{searchQuery}</span>"
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {searchResults.map((city) => (
                    <button
                      key={city}
                      onClick={() => handleSelect(city)}
                      className={`text-left px-3.5 py-2.5 rounded-lg text-sm font-medium transition border ${
                        currentCity === city
                          ? "bg-indigo-50 border-indigo-300 text-indigo-600"
                          : "bg-white border-gray-150 text-gray-700 hover:border-indigo-200 hover:bg-indigo-50/40"
                      }`}
                    >
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* ── All India Option ── */}
              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => handleSelect("All India")}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border transition ${
                    currentCity === "All India"
                      ? "bg-indigo-50 border-indigo-300 text-indigo-600 font-semibold"
                      : "bg-white border-gray-200 text-gray-700 hover:border-indigo-200 hover:bg-indigo-50/30"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center">
                      <Globe className="w-4 h-4" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900">All India</div>
                      <div className="text-[11px] text-gray-500">Show ads from all cities across India</div>
                    </div>
                  </div>
                  {currentCity === "All India" && (
                    <span className="text-xs font-semibold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded">Selected</span>
                  )}
                </button>
              </div>

              {/* ── Popular Cities with landmark icons ── */}
              <div className="mb-2">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5">Popular Cities</h3>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
                {TOP_ICON_CITIES.map((city) => {
                  const isSelected = currentCity === city;
                  return (
                    <button
                      key={city}
                      onClick={() => handleSelect(city)}
                      className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border transition group ${
                        isSelected
                          ? "bg-indigo-50 border-indigo-300 text-indigo-600"
                          : "bg-white border-gray-200 text-gray-500 hover:border-indigo-200 hover:bg-indigo-50/30 hover:text-indigo-500"
                      }`}
                    >
                      <div className="transition-transform group-hover:scale-105">
                        {CityLandmarks[city] || <Navigation className="w-10 h-10" />}
                      </div>
                      <span className={`text-xs font-semibold text-center leading-tight ${
                        isSelected ? "text-indigo-600" : "text-gray-700 group-hover:text-indigo-600"
                      }`}>
                        {city}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* ── Remaining Popular Cities (text pills) ── */}
              <div className="flex flex-wrap gap-2 mb-6">
                {POPULAR_CITIES.filter(c => !(TOP_ICON_CITIES as readonly string[]).includes(c)).map((city) => (
                  <button
                    key={city}
                    onClick={() => handleSelect(city)}
                    className={`px-3.5 py-2 rounded-lg text-xs font-semibold border transition ${
                      currentCity === city
                        ? "bg-indigo-50 border-indigo-300 text-indigo-600"
                        : "bg-gray-50 border-gray-200 text-gray-600 hover:border-indigo-200 hover:bg-indigo-50/40 hover:text-indigo-600"
                    }`}
                  >
                    {city}
                  </button>
                ))}
              </div>

              {/* ── All Cities ── */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">All Cities</h3>

                {/* Alphabet bar */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {ALPHABET.map((letter) => {
                    const hasCity = !!citiesByLetter[letter];
                    const isActive = activeLetter === letter;
                    return (
                      <button
                        key={letter}
                        disabled={!hasCity}
                        onClick={() => scrollToLetter(letter)}
                        className={`w-7 h-7 rounded text-xs font-bold transition ${
                          isActive
                            ? "bg-indigo-600 text-white"
                            : hasCity
                              ? "text-indigo-600 hover:bg-indigo-50"
                              : "text-gray-300 cursor-default"
                        }`}
                      >
                        {letter}
                      </button>
                    );
                  })}
                </div>

                {/* City list by letter */}
                <div className="space-y-4">
                  {ALPHABET.map((letter) => {
                    const cities = citiesByLetter[letter];
                    if (!cities) return null;
                    return (
                      <div
                        key={letter}
                        ref={(el) => { letterRefs.current[letter] = el; }}
                      >
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1">
                          {cities.map((city) => (
                            <button
                              key={city}
                              onClick={() => handleSelect(city)}
                              className={`text-left py-1.5 text-sm transition rounded px-1 -mx-1 ${
                                currentCity === city
                                  ? "text-indigo-600 font-semibold bg-indigo-50"
                                  : "text-gray-600 hover:text-indigo-600 hover:bg-indigo-50/50"
                              }`}
                            >
                              {city}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CitySelectionModal;
